import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useShallow } from 'zustand/react/shallow';

import { isCustomerFromRussia } from 'utils/currency.utils';
import { addLocalePrefix, replacePathParams } from 'utils/routes.utils';
import { listScrollGradient } from 'utils/styles.utils';
import {
  getAllItemsCount,
  isSeatedItem,
  toTotalPrice,
} from 'utils/ticket.utils';

import { IS_LOYALTY_ENABLED } from 'constants/loyalty.constants';
import { Routes } from 'constants/routes.constants';
import { MAX_CART_ITEMS } from 'constants/tickets.constants';
import { useLocale } from 'hooks/useLocale';
import { useModalControl } from 'hooks/useModalControl';
import { useAccount } from 'queries/accounts/useAccount';
import { useSimulateLoyaltyCartUpdate } from 'queries/tickets/useSimulateLoyaltyCartUpdate';
import { intlIdForTicketingError } from 'queries/tickets/utils';
import { useTrackBeginCheckout } from 'services/datalayer/ecomm/useTrackBeginCheckout';
import { useTrackQuantityChange } from 'services/datalayer/ecomm/useTrackQuantityChange';
import { useTrackViewItemList } from 'services/datalayer/ecomm/useTrackViewItemList';
import { useCartStore } from 'services/stores/cart';
import { PageEventFragment } from 'types/api/dato-operations.types';
import { TicketFragment } from 'types/api/tickets.types';
import {
  Categories,
  LocalCartItemPricing,
  TICKET_FILTERS_ALL,
} from 'types/tickets.types';

import Authentication from 'components/@account/Authentication';
import { AffiliateNotice } from 'components/@affiliate/AffiliateNotice';
import { useAffiliateWatcher } from 'components/@affiliate/useAffiliateWatcher';
import BackButton from 'components/@common/BackButton';
import LanguagePickerButton from 'components/@common/LanguagePickerButton';
import EventHeader from 'components/@events/EventHeader';
import EventImageBackgroundHeader from 'components/@events/EventImageBackgroundHeader';
import EventSideBarSticky from 'components/@events/EventSideBarSticky';
import TicketFilters from 'components/@events/TicketFilters/TicketFilters';
import TopNavigation from 'components/@navigation/TopNavigation';
import SeatingDialog from 'components/@seating/SeatingDialog';
import SeatsTimer from 'components/@seating/SeatsTimer';
import { useTicketOffers } from 'components/@ticket-offer/hooks';
import CartOutOfStockAlert from 'components/@tickets/CartOutOfStockAlert';
import { useTicketInfoLookup } from 'components/@tickets/hooks';
import { useWatchTicketActiveOrder } from 'components/@tickets/Payment/useWatchTicketActiveOrder';
import PushAppDialog from 'components/@tickets/PushAppDialog';

import CartCheckoutButton from './CartCheckoutButton';
import { useAppPushState, useCreateOrReplaceCart } from './hooks';
import OrderSummaryDialog from './OrderSummaryDialog';
import RussianUserNotice from './RussianUserNotice';
import TicketList from './TicketList';
import {
  castStringToCategoryKey,
  getHoldToken,
  mapMatchingSeatedTickets,
} from './utils';

interface Props {
  event: PageEventFragment;
  categories: Categories;
  filters: Array<string>;
  ticketingError?: boolean;
}

const EventDetailCartContainer = ({
  event,
  categories,
  filters,
  ticketingError,
}: Props) => {
  const { locale } = useLocale();
  const intl = useIntl();
  const { account, isSignedIn } = useAccount();
  const { enqueueSnackbar } = useSnackbar();
  const {
    open: openRussianNotice,
    onClose: onCloseRussianNotice,
    onOpen: onOpenRussianNotice,
  } = useModalControl();
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const router = useRouter();

  const countryCode = account?.countryCode;
  const { yandexUrl } = event;

  /**
   * Affiliate watcher - syncs affiliate
   */
  useAffiliateWatcher();

  const [seatingToken, seatingInfo, eventKey, cartItems, ticketingCartId] =
    useCartStore(
      useShallow((state) => [
        state.seatingToken,
        state.seatingInfo,
        state.eventKey,
        state.items,
        state.ticketingCartId,
      ]),
    );

  const {
    setSeatingInfo,
    addItemToCart,
    removeItemFromCart,
    removeLocalItemFromCart,
  } = useCartStore((state) => state.actions);
  const lookup = useTicketInfoLookup();

  /**
   * Watch active order after checkout
   */
  useWatchTicketActiveOrder({
    toggleLoading: (state: boolean) => setIsLoadingCheckout(state),
  });

  const {
    open: openAuthentication,
    onClose: onCloseAuthentication,
    onOpen: onOpenAuthentication,
  } = useModalControl();
  const {
    open: openOrderSummary,
    onClose: onCloseOrderSummary,
    onOpen: onOpenOrderSummary,
  } = useModalControl();
  const {
    open: openPushApp,
    onClose: onClosePushApp,
    onOpen: onOpenPushApp,
  } = useModalControl();

  const {
    ticketOffers,
    isLoading: isLoadingTicketOffers,
    isEnabled,
  } = useTicketOffers();

  const { createOrReplaceCart, isLoading: isLoadingCart } =
    useCreateOrReplaceCart();
  const { showAppPushDialog, setAppPushSoftIgnored } = useAppPushState(
    eventKey,
    event.ticketsPushApp,
    !!ticketOffers?.length,
  );

  useTrackViewItemList(categories);
  const trackCheckout = useTrackBeginCheckout();
  const { trackAdd, trackRemove } = useTrackQuantityChange();

  const [activeFilter, setActiveFilter] = useState<string>(TICKET_FILTERS_ALL);

  const mergedCategories = useMemo(() => {
    if (!ticketOffers?.length || !isEnabled) return categories;
    return [...ticketOffers, ...categories];
  }, [categories, isEnabled, ticketOffers]);

  const cartItemsCount = useMemo(
    () => getAllItemsCount(cartItems),
    [cartItems],
  );

  const handleAppPushSoftIgnored = () => {
    setAppPushSoftIgnored('true');
    onClosePushApp();
  };

  const handleSelectSeatingForTicket = (ticket: TicketFragment) => {
    if (showAppPushDialog) return onOpenPushApp();
    if (!isSignedIn) return onOpenAuthentication();
    if (!ticket.seatingData) return;

    const chartKey = ticket.seatingData.seatingChartEventOrSeasonId;
    const categoryKey = ticket.seatingData.seatingCategoryKey;

    // Find all categories that match the clicked ticket
    const mappedCategories = mapMatchingSeatedTickets(
      mergedCategories,
      chartKey,
    );
    const timeslots = [
      ...new Set(mappedCategories.flatMap((c) => c.timeslots)),
    ].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // Find if the ticket is already in the cart
    const existingCartItem = cartItems.find(
      (item) => item.catalogueItemId === ticket.id,
    );

    // Determine categories already present in the cart
    const selectedCategories = new Set(
      cartItems
        .filter((item) => item.quantity > 0)
        .map((item) => {
          const category = mergedCategories.find((category) =>
            category.tickets.some((t) => t.id === item.catalogueItemId),
          );
          return category ? category.id : null;
        }),
    );

    const ticketCategory = mergedCategories.find((category) =>
      category.tickets.some((t) => t.id === ticket.id),
    );

    const isAllowedToAdd =
      existingCartItem ||
      (ticketCategory && selectedCategories.has(ticketCategory.id));

    // If a seat from the same category is already in the cart, always allow adding or deselecting additional seats.
    // Otherwise, restrict adding seats if MAX_CART_ITEMS is reached.
    if (!isAllowedToAdd && cartItemsCount >= MAX_CART_ITEMS) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'general.cart.max_items.label' },
          { amount: MAX_CART_ITEMS },
        ),
        { variant: 'warning' },
      );
      return;
    }

    setSeatingInfo({
      chartKey,
      focusCategoryKey: [castStringToCategoryKey(categoryKey)],
      categories: mappedCategories,
      dateTimeRange: {
        start: timeslots.at(0),
        end: timeslots.at(-1),
      },
    });
  };

  const handleCheckout = () => {
    if (showAppPushDialog) {
      onOpenPushApp();
      return;
    }
    if (!isSignedIn) {
      onOpenAuthentication();
      return;
    }
    setIsLoadingCheckout(true);

    const holdToken = getHoldToken(seatingToken, cartItems);

    createOrReplaceCart(
      { items: cartItems, holdToken },
      {
        onSuccess: ({ cart }) => {
          if (cart) {
            trackCheckout(cart);
            router.push(
              replacePathParams(Routes.EventCheckout, { slug: event.slug }),
            );
          }
        },
        onError: (errors) => {
          setIsLoadingCheckout(false);
          enqueueSnackbar(
            intl.formatMessage({ id: intlIdForTicketingError('cart', errors) }),
            { variant: 'error' },
          );
        },
      },
    );
  };

  // Ensure we always have a ticketingCart for the user once he starts adding items
  useEffect(() => {
    if (!IS_LOYALTY_ENABLED) return;
    if (ticketingCartId || !cartItems?.length || isLoadingCart) return;
    const holdToken = getHoldToken(seatingToken, cartItems);
    createOrReplaceCart({ items: cartItems, holdToken }, {});
  }, [
    ticketingCartId,
    seatingToken,
    cartItems,
    createOrReplaceCart,
    isLoadingCart,
  ]);

  const handleAddTicket = (ticket: TicketFragment) => {
    if (showAppPushDialog) return onOpenPushApp();
    if (!isSignedIn) return onOpenAuthentication();
    if (cartItemsCount >= MAX_CART_ITEMS) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'general.cart.max_items.label' },
          { amount: MAX_CART_ITEMS },
        ),
        { variant: 'warning' },
      );
      return;
    }

    trackAdd(ticket);
    addItemToCart({
      catalogueItemId: ticket.id,
      name: ticket.name,
      quantity: 1,
    });
  };

  const handleRemoveTicket = (ticket: TicketFragment, quantity: number = 1) => {
    if (!isSignedIn) return onOpenAuthentication();
    trackRemove(ticket, quantity);
    removeItemFromCart({
      catalogueItemId: ticket.id,
      quantity,
    });
  };

  const itemsWithPrice = useMemo(() => {
    return cartItems.map<LocalCartItemPricing>((item) => {
      const ticket = lookup(item.catalogueItemId);
      return {
        ...item,
        totalPrice: toTotalPrice(ticket.discountedPrice, item.quantity),
        subtotalPrice: toTotalPrice(ticket.originalPrice, item.quantity),
      };
    });
  }, [cartItems, lookup]);

  const cartTotalPrice = useMemo(() => {
    return (
      itemsWithPrice.reduce((acc, item) => {
        return acc + item.totalPrice?.amount;
      }, 0) ?? 0
    );
  }, [itemsWithPrice]);

  const { discountValue: loyaltyDiscount, isLoading: isLoyaltyEffectLoading } =
    useSimulateLoyaltyCartUpdate(
      ticketingCartId,
      cartItems.map((item) => ({
        catalogueItemId: item.catalogueItemId,
        quantity: item.quantity,
      })),
    );

  const cartTotalDiscountedPrice = cartTotalPrice - loyaltyDiscount;

  const handleDeleteItemOrderSummary = (item: LocalCartItemPricing) => {
    const ticket = mergedCategories
      .flatMap((category) => category.tickets)
      .find((t) => t.id === item.catalogueItemId);

    //  Delete local ticket if it no longer exists in catalog
    if (!ticket) {
      removeLocalItemFromCart(item);
      return;
    }

    if (isSeatedItem(item?.bookObjectsInfo)) {
      handleSelectSeatingForTicket(ticket);
    } else {
      handleRemoveTicket(ticket, item.quantity);
    }
  };

  const handleFilterChange = useCallback(
    (filter: string) => {
      setActiveFilter(filter);

      const query = { ...router.query };

      if (filter === TICKET_FILTERS_ALL) {
        delete query.category;
      } else {
        query.category = filter;
      }

      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const filteredCategories = useMemo(() => {
    if (activeFilter === TICKET_FILTERS_ALL) return mergedCategories;
    return mergedCategories.filter(({ name }) => name === activeFilter);
  }, [activeFilter, mergedCategories]);

  useEffect(() => {
    if (filters && filters?.length > 0) {
      const categoryParam = router.query.category as string;

      if (categoryParam && filters.includes(categoryParam)) {
        handleFilterChange(categoryParam);
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, router.query.category]);

  useEffect(() => {
    if (isCustomerFromRussia(countryCode) && yandexUrl) {
      onOpenRussianNotice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, yandexUrl]);

  return (
    <>
      <TopNavigation>
        <BackButton
          href={replacePathParams(Routes.EventDetail, { slug: event.slug })}
        />
        <Stack
          sx={{
            gap: 1,
            flexDirection: 'row',
          }}
        >
          {/* Ensures we dont show an expired timer after the user has confirmed their seats are gone */}
          {!seatingToken?.expirationHandled && (
            <SeatsTimer endTime={seatingToken?.expiresAt} />
          )}
          <LanguagePickerButton />
        </Stack>
      </TopNavigation>

      <EventImageBackgroundHeader event={event} />

      <Container sx={{ position: 'relative', flex: 1, px: { xs: 0, md: 2 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '6fr 1fr 5fr' },

            gridTemplateAreas: {
              xs: `'header' 'cartItems'`,
              md: `"header . cover" "cartItems . cover"`,
            },

            alignItems: 'start',
            py: { md: 15 },
          }}
        >
          <EventSideBarSticky event={event}>
            <CartCheckoutButton
              cartItemsCount={cartItemsCount}
              cartTotalOriginalPrice={cartTotalPrice}
              cartTotalDiscountedPrice={cartTotalDiscountedPrice}
              isLoadingCheckout={isLoadingCheckout}
              isLoyaltyEffectLoading={isLoyaltyEffectLoading}
              onCheckout={handleCheckout}
              onOpenOrderSummary={onOpenOrderSummary}
            />
          </EventSideBarSticky>

          <Stack
            sx={{
              display: { xs: 'none', md: 'block' },
            }}
          >
            <Box mb={{ md: '24px' }}>
              <AffiliateNotice eventName={event.title} />
            </Box>
            <EventHeader event={event} gradientCover />
          </Stack>

          <Box
            sx={{
              mt: { md: 3 },
              gridArea: 'cartItems',
              overflow: 'hidden',
            }}
          >
            {!!filteredCategories.length && filters?.length > 1 && (
              <TicketFilters
                activeFilter={activeFilter}
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            )}
            {!!filteredCategories.length ? (
              <TicketList
                categories={filteredCategories}
                eventTicketStatus={event.ticketsStatus}
                isFilterActive={activeFilter !== TICKET_FILTERS_ALL}
                cartItems={cartItems}
                isLoadingOffers={isLoadingTicketOffers}
                onSelectSeatingForTicket={handleSelectSeatingForTicket}
                onAddItem={handleAddTicket}
                onRemoveItem={handleRemoveTicket}
              />
            ) : (
              <Alert severity="info" sx={{ mx: { xs: 2, md: 0 } }}>
                <AlertTitle>
                  <FormattedMessage
                    id={
                      ticketingError
                        ? 'general.something_went_wrong.label'
                        : 'event.tickets.label.available.yet'
                    }
                  />
                </AlertTitle>
                <FormattedMessage id="event.tickets.label.comingsoon" />
              </Alert>
            )}
            <CartOutOfStockAlert sx={{ mt: 2, mx: { xs: 2, md: 0 } }} />
          </Box>
        </Box>
      </Container>
      {!!mergedCategories.length && (
        <Box
          sx={{
            mt: 3,
            display: { xs: 'block', md: 'none' },
            position: 'sticky',
            bottom: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 2,
              ...listScrollGradient(),
            }}
          >
            <CartCheckoutButton
              cartItemsCount={cartItemsCount}
              cartTotalOriginalPrice={cartTotalPrice}
              isLoadingCheckout={isLoadingCheckout}
              isLoyaltyEffectLoading={isLoyaltyEffectLoading}
              cartTotalDiscountedPrice={cartTotalDiscountedPrice}
              onCheckout={handleCheckout}
              onOpenOrderSummary={onOpenOrderSummary}
            />
          </Box>
        </Box>
      )}
      {seatingInfo && <SeatingDialog event={event} />}
      {!isSignedIn && (
        <Authentication
          titleIntlId="account.sign.in.checkout.label"
          drawer={{
            open: openAuthentication,
            onClose: onCloseAuthentication,
          }}
          afterSignInUrl={addLocalePrefix(locale, Routes.EventCart, {
            slug: event.slug,
          })}
          originSourceCategory="Cart"
        />
      )}
      {event.ticketsPushApp !== 'disabled' && !ticketOffers?.length && (
        <PushAppDialog
          open={openPushApp}
          onClose={onClosePushApp}
          onSoftCtaClick={handleAppPushSoftIgnored}
          variant={event.ticketsPushApp}
          eventSlug={event.slug}
        />
      )}
      <OrderSummaryDialog
        open={openOrderSummary}
        onClose={onCloseOrderSummary}
        cartTotalOriginalPrice={cartTotalPrice}
        cartTotalDiscountedPrice={cartTotalDiscountedPrice}
        cartItemsCount={cartItemsCount}
        isLoadingCheckout={isLoadingCheckout}
        isLoyaltyEffectLoading={isLoyaltyEffectLoading}
        onCheckout={handleCheckout}
        onDeleteItem={handleDeleteItemOrderSummary}
        items={itemsWithPrice}
      />
      <Dialog
        sx={{
          direction: 'ltr /*! @noflip */',
        }}
        open={openRussianNotice}
        onClose={onCloseRussianNotice}
      >
        <DialogTitle
          sx={{
            py: (theme) => (theme.direction === 'ltr' ? 4 : 3.5),
            px: (theme) => (theme.direction === 'ltr' ? 3 : 12),
          }}
        >
          <FormattedMessage id="event.notice.russian.title" />
        </DialogTitle>
        <DialogContent>
          <RussianUserNotice event={event} onClose={onCloseRussianNotice} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventDetailCartContainer;
